<template>
  <div :key="`trigger-menu-${triggerId}`" ref="el" class="trigger-menu">
    <item-header type="incidentTrigger" @close="$emit('close')">
      <editable-item-title :item="trigger" :placeholder="$t('objectNames.trigger')" />
    </item-header>

    <drawer-block
      v-model:open="drawerStates.triggerGeneral"
      :title="$t('labels.general')"
    >
      <div class="column gap-4">
        <input-text
          ref="title"
          :label="$t('labels.title')"
          :value="trigger.title"
          @enter="descriptionField?.focus()"
          @update:value="updateKey('title', $event)"
        />

        <input-texteditor
          ref="descriptionField"
          :value="trigger.description"
          :label="$t('labels.description')"
          @update:value="updateKey('description', $event)"
        />
      </div>
    </drawer-block>

    <drawer-block
      v-model:open="drawerStates.triggerConditions"
      :title="$t('objectNames.condition', conditions.length)"
    >
      <div class="column">
        <div>
          <p v-if="conditions.length === 0" class="message-info mv-1" style="">
            {{ $t("texts.noTriggerConditions") }}
          </p>
          <condition-list
            src-type="incidentTrigger"
            :src-id="trigger.id"
            :available-activator-types="[
              'target',
              'resource',
              'index',
              'round',
              'roundRange',
              'action',
              'incident'
            ]"
            :selected-condition="selectedCondition"
            @select="select"
            @deleted="deselect"
            @created="adjustSettings($event)"
            @copy="emit('copy', $event)"
          />
        </div>

        <div class="trigger-menu__line">
          <span> {{ $t("labels.occurrenceProbability") }}{{ $t(":") }} </span>
          <input-number
            :max="100"
            :min="0"
            :value="trigger.chance"
            step="any"
            class="input_inline input_small"
            @update:value="updateKey('chance', $event)"
          />
          <span>%</span>
        </div>

        <!-- TRIGGER MAX OCCURRENCE -->
        <div class="trigger-menu__line">
          <span> {{ $t("labels.maxOccurrence") }}{{ $t(":") }} </span>
          <input-number
            :allow-null="true"
            :min="1"
            :value="trigger.maxOccurrences ?? undefined"
            :icons="trigger.maxOccurrences != null ? ['x'] : []"
            placeholder="∞"
            class="input_inline input_small"
            @update:value="updateKey('maxOccurrences', $event)"
            @click:x="updateKey('maxOccurrences', null)"
          />
        </div>
      </div>
    </drawer-block>
    <menu-group>
      <button
        class="button button_secondary"
        type="button"
        @click="deleteTrigger"
      >
        {{ $t("actions.deleteTrigger") }}
      </button>
    </menu-group>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import MenuGroup from "@core/ui/MenuGroup.vue";
import InputText from "@core/input/InputText.vue";
import InputTexteditor from "@core/input/TextEditor.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import DrawerBlock from "@core/ui/DrawerBlock.vue";
import InputNumber from "@core/input/InputNumber.vue";
import ConditionList from "../condition/ConditionList.vue";
import EditableItemTitle from "@editor/components/EditableItemTitle.vue";

import { useIncidentTriggerStore } from "./incidentTriggers.store";
import { useConditionStore } from "../condition/conditions.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";

import type { IncidentTriggerItem } from "./IncidentTriggerItem";
import type { ConditionItem } from "../condition/ConditionItem";

const props = defineProps<{
  triggerId: number;
  selectedCondition?: { type: "condition"; id: number };
  open?: boolean;
}>();

const emit = defineEmits<{
  (event: "select", item: ConditionItem): void;
  (event: "deselect", item: ConditionItem): void;
  (event: "close"): void;
  (event: "copy", item: ConditionItem): void;
}>();

const descriptionField = ref<InstanceType<typeof InputTexteditor>>();

const drawerStates = useDrawerStates();
const triggerStore = useIncidentTriggerStore();
const conditionStore = useConditionStore();

const conditions = computed(() => {
  return conditionStore.getBySource({
    type: "incidentTrigger",
    id: props.triggerId
  });
});

const trigger = computed(() => {
  return triggerStore.items[props.triggerId];
});

function updateKey<K extends keyof IncidentTriggerItem>(
  key: K,
  value: IncidentTriggerItem[K]
) {
  triggerStore.updateKey(props.triggerId, key, value);
}

function deleteTrigger() {
  triggerStore.delete({ id: props.triggerId });
}

/**
 * Adjusts the settings of the trigger based on the condition.
 */
function adjustSettings(condition: ConditionItem) {
  let maxOccurrences = trigger.value.maxOccurrences;
  let chance = trigger.value.chance;

  if (maxOccurrences == null) {
    if (condition.activatorType === "round") maxOccurrences = 1;
    else if (condition.activatorType === "roundRange") maxOccurrences = 1;
  }
  if (chance == null) {
    chance = 50;
  }

  triggerStore.update({
    id: props.triggerId,
    maxOccurrences: maxOccurrences,
    chance: chance
  });
}

function select(item: ConditionItem) {
  emit("select", item);
}
function deselect(item: ConditionItem) {
  emit("deselect", item);
}
</script>

<style lang="scss">
.trigger-menu {
  &__line {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 4.8rem 1rem;
    gap: 0.25rem;
    align-items: center;
  }
}
</style>
